import type { ActivityLocation, DayRow } from '$/dto/ItineraryData.dto.ts';
import orderActivitiesByPartOfDay from '$/utils/orderActivitiesByPartOfDay.ts';

export const isUsableLocation = (location: unknown): location is ActivityLocation => {
  if (!location || typeof location !== 'object') return false;

  const candidate = location as Partial<ActivityLocation>;

  return typeof candidate.name === 'string' && candidate.name.trim().length > 0;
};

export const getLocationLabel = (location: ActivityLocation): string =>
  [location.name, location.city, location.country_code].filter(Boolean).join(', ');

export const hasCoordinates = (
  location: ActivityLocation
): location is ActivityLocation & { latitude: number; longitude: number } =>
  typeof location.latitude === 'number' &&
  Number.isFinite(location.latitude) &&
  typeof location.longitude === 'number' &&
  Number.isFinite(location.longitude);

export const getLocationQuery = (location: ActivityLocation): string => {
  if (hasCoordinates(location)) {
    return `${location.latitude},${location.longitude}`;
  }

  return location.formatted_address?.trim() || getLocationLabel(location);
};

export const locationKey = (location: ActivityLocation): string =>
  location.geoapify_place_id?.trim() ||
  location.google_place_id?.trim() ||
  getLocationQuery(location).trim().toLowerCase();

const placeIdOf = (location: ActivityLocation): string | null => location.google_place_id?.trim() || null;

export const hasStableIdentity = (location: ActivityLocation): boolean =>
  hasCoordinates(location) ||
  Boolean(location.geoapify_place_id?.trim()) ||
  Boolean(location.google_place_id?.trim());

export const getUniqueLocations = (activities: DayRow[]): ActivityLocation[] => {
  const seen = new Set<string>();

  return orderActivitiesByPartOfDay(activities).reduce<ActivityLocation[]>((locations, activity) => {
    const location = activity.location;

    if (!isUsableLocation(location)) {
      return locations;
    }

    const key = locationKey(location);

    if (!seen.has(key)) {
      seen.add(key);
      locations.push(location);
    }

    return locations;
  }, []);
};

const DEPARTURE_BOOKING_SUFFIX = '::departure';

const isHotelLocation = (location: ActivityLocation, hotelName: string): boolean => {
  const locationName = location.name.trim().toLowerCase();
  const normalisedHotelName = hotelName.trim().toLowerCase();

  return (
    locationName === normalisedHotelName ||
    locationName.includes(normalisedHotelName) ||
    normalisedHotelName.includes(locationName)
  );
};

const findFlightEndpoint = (
  activities: DayRow[],
  flightBookingIds: ReadonlySet<string>,
  moment: 'arrival' | 'departure'
): ActivityLocation | null => {
  const activity = orderActivitiesByPartOfDay(activities).find((row) => {
    const bookingId = row.booking_id;

    if (!bookingId || !isUsableLocation(row.location)) return false;

    if (moment === 'arrival') {
      return flightBookingIds.has(bookingId);
    }

    return [...flightBookingIds].some((flightId) => bookingId === `${flightId}${DEPARTURE_BOOKING_SUFFIX}`);
  });

  return activity && isUsableLocation(activity.location) ? activity.location : null;
};

/**
 * Builds the per-day "open in Google Maps" route. Only a location with
 * verified coordinates may anchor or appear on the route itself — an
 * optional, unverified activity (no confirmed coordinates) is omitted from
 * routing entirely rather than sent to a guessed destination. This never
 * affects the real anchors (a flight endpoint or the confirmed
 * accommodation), which come from a booking or the trip's accommodation
 * data, not an arbitrary suggestion, and may still use a text-address
 * fallback when they lack coordinates of their own.
 */
export const buildGoogleMapsUrl = (
  activities: DayRow[],
  flightBookingIds: ReadonlySet<string>,
  accommodationPlace?: string | null,
  accommodationLocation?: ActivityLocation | null,
  destination?: string | null
): string | null => {
  const locations = getUniqueLocations(activities);

  if (locations.length === 0) return null;

  const routableLocations = locations.filter(hasStableIdentity);

  const hotelName = accommodationPlace?.trim();
  const hotelLocation: ActivityLocation | null = hotelName
    ? {
        ...accommodationLocation,
        name: hotelName,
        city: accommodationLocation?.city || locations[0].city || destination?.trim() || '',
        country_code: accommodationLocation?.country_code || locations[0].country_code || '',
      }
    : null;

  const arrivalLocation = findFlightEndpoint(activities, flightBookingIds, 'arrival');
  const departureLocation = findFlightEndpoint(activities, flightBookingIds, 'departure');

  const origin = arrivalLocation ?? hotelLocation ?? routableLocations[0] ?? null;
  const routeDestination =
    departureLocation ?? hotelLocation ?? routableLocations[routableLocations.length - 1] ?? origin;

  if (!origin || !routeDestination) return null;

  const originKey = locationKey(origin);
  const destinationKey = locationKey(routeDestination);

  const waypoints = routableLocations.filter((location) => {
    const key = locationKey(location);

    if (key === originKey || key === destinationKey) return false;

    if (
      hotelLocation &&
      hotelName &&
      (origin === hotelLocation || routeDestination === hotelLocation) &&
      isHotelLocation(location, hotelName)
    ) {
      return false;
    }

    return true;
  });

  if (originKey === destinationKey && waypoints.length === 0 && !hotelLocation) {
    const searchUrl = new URL('https://www.google.com/maps/search/');
    searchUrl.searchParams.set('api', '1');
    searchUrl.searchParams.set('query', getLocationQuery(origin));

    const originPlaceId = placeIdOf(origin);
    if (originPlaceId) searchUrl.searchParams.set('query_place_id', originPlaceId);

    return searchUrl.toString();
  }

  const directionsUrl = new URL('https://www.google.com/maps/dir/');
  directionsUrl.searchParams.set('api', '1');
  directionsUrl.searchParams.set('origin', getLocationQuery(origin));

  const originPlaceId = placeIdOf(origin);
  if (originPlaceId) directionsUrl.searchParams.set('origin_place_id', originPlaceId);

  directionsUrl.searchParams.set('destination', getLocationQuery(routeDestination));

  const destinationPlaceId = placeIdOf(routeDestination);
  if (destinationPlaceId) directionsUrl.searchParams.set('destination_place_id', destinationPlaceId);

  if (waypoints.length > 0) {
    directionsUrl.searchParams.set('waypoints', waypoints.map(getLocationQuery).join('|'));

    const waypointPlaceIds = waypoints.map(placeIdOf);
    if (waypointPlaceIds.every(Boolean)) {
      directionsUrl.searchParams.set('waypoint_place_ids', waypointPlaceIds.join('|'));
    }
  }

  return directionsUrl.toString();
};
